// OCR 失敗理由（ocrFailedReason）の表示解決ユーティリティ
// （Issue #475 Phase 3/5、Issue #550、Issue #600）
//
// api-server は EvidenceFile / ExtractionSource の応答に `ocrFailedReason` を
// `usage_limit_exceeded | ai_server_error | conversion_error | null` で載せる。
// viewer は「AI の利用上限に到達した」「変換に失敗した」ことを他の OCR 失敗と
// **出し分けて** 表示する（ユーザーが取るべき行動が理由ごとに変わるため）。
//
// `conversion_error` は PDF 変換失敗（ConvertToPreviewFailed webhook）由来で、
// 総括表（ExtractionSource）向けに Issue #550 で追加した。証憑ファイル
// （EvidenceFile）の変換失敗も Issue #600 で同じ OCR 失敗へ合流させたため、
// **両方**がこの値を取る（旧仕様のアップロードエラー経路は到達不能だった）。

/**
 * OCR 失敗理由の値（`@domain` の `OcrFailedReasonValues` と同値）。
 * viewer は `@domain` を import できないため同値の union をローカル定義する。
 */
export const OCR_FAILED_REASON_VALUES = [
  'usage_limit_exceeded',
  'ai_server_error',
  'conversion_error',
] as const;

export type OcrFailedReason = (typeof OCR_FAILED_REASON_VALUES)[number];

/** OCR 失敗のユーザー向け表示 */
export type OcrFailureDisplay = {
  /** バッジ等に出す短いラベル */
  label: string;
  /** tooltip / 補足に出す説明文 */
  description: string;
};

/**
 * OCR 失敗理由の 4 状態それぞれの表示。
 *
 * `null`（理由不明）は **Phase 3 以前に記録された古いデータ** で起こる。
 * 「上限到達」に寄せず中立に表示する（「上限に達した」と嘘をつかない）。
 *
 * `usage_limit_exceeded` は追加付与まで解消しない恒久エラーなので、
 * **リトライを示唆する文言を使わない**。
 *
 * `conversion_error` は逆に、ファイルを直して再アップロードすれば解消しうるので
 * 再アップロードを促す。ただし **ファイル起因と断定しない**（AI サーバー側の
 * 変換バグの可能性もあるため、解決しない場合の問い合わせ先も添える）。
 *
 * 本表示は証憑一覧（`app-file-processing-status-badge`）でのみ使われる。証憑には
 * 差し替え UI が無く（Issue #396 で削除）、OCR 失敗した証憑を直す唯一の導線は
 * **行を削除してから再アップロード** なので、文言はその手順を明示する
 * （「再度アップロード」だけでは、ユーザーが同名ファイルを追加して二重登録になる）。
 * 総括表側の同理由は `extraction-status-banner` が別文言で扱う。
 */
const UNKNOWN_REASON_DISPLAY: OcrFailureDisplay = {
  label: 'OCR 失敗',
  description: 'OCR に失敗しました。失敗理由は記録されていません。',
};

const REASON_DISPLAYS: Record<OcrFailedReason, OcrFailureDisplay> = {
  usage_limit_exceeded: {
    label: 'OCR 失敗（利用上限）',
    description:
      'AI の利用上限に達しているため OCR を実行できませんでした。管理者にお問い合わせください。',
  },
  ai_server_error: {
    label: 'OCR 失敗（AI エラー）',
    description:
      '処理中にエラーが発生したため OCR に失敗しました。時間をおいて再度お試しいただくか、管理者にお問い合わせください。',
  },
  conversion_error: {
    label: 'OCR 失敗（変換エラー）',
    description:
      'ファイルの変換に失敗しました。ファイルが破損していないかご確認のうえ、このファイルを削除してから再度アップロードしてください。解決しない場合は管理者にお問い合わせください。',
  },
};

/**
 * API から受け取った失敗理由を `OcrFailedReason | null` へ正規化する。
 *
 * 既知の値以外（キー欠落 / null / 将来 api-server 側に追加される新しい値）は
 * すべて `null`（理由不明）へ落とす。**未知の値を上限到達に寄せない**。
 *
 * @param value API 応答の `ocrFailedReason`（型不明）
 */
export function parseOcrFailedReason(value: unknown): OcrFailedReason | null {
  return typeof value === 'string' &&
    (OCR_FAILED_REASON_VALUES as readonly string[]).includes(value)
    ? (value as OcrFailedReason)
    : null;
}

/**
 * OCR 失敗理由からユーザー向けのラベル・説明文を解決する。
 *
 * @param reason 失敗理由。`null` / `undefined` は「理由不明」として中立に表示する
 */
export function describeOcrFailure(
  reason: OcrFailedReason | null | undefined,
): OcrFailureDisplay {
  return reason === null || reason === undefined
    ? UNKNOWN_REASON_DISPLAY
    : REASON_DISPLAYS[reason];
}
